/**
 * IndemnitorSection - Single indemnitor with nested collaterals
 * Rendered per-indemnitor by CreateLargeBondModal
 */

import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { OathInput, OathSelect, OathButton } from '../../../shared/OathUI';
import { STATE_OPTIONS } from '../../transferbonds/shared/usStates';
import { COLLATERAL_TYPES, collateralTypeLabel, LARGE_BOND_FIELD_LIMITS as LIMITS } from '../shared/largeBondConstants';
import CurrencyInput from '../shared/CurrencyInput';

const IndemnitorSection = ({ indemnitor, index, dispatch, onRemove, canRemove, disabled, defendantAddress, isCollateralTypeTaken }) => {
  // Collateral types still available to a given collateral row: anything not
  // already used by another collateral on THIS indemnitor (deeds always),
  // plus the row's own current value so existing data still displays.
  const typeOptionsFor = (coll, collIndex) => {
    if (!isCollateralTypeTaken) return COLLATERAL_TYPES;
    return COLLATERAL_TYPES.filter(
      (t) => t.value === coll.collateralType || !isCollateralTypeTaken(t.value, index, collIndex)
    );
  };
  const duplicateTypeError = (coll, collIndex) =>
    isCollateralTypeTaken && coll.collateralType && isCollateralTypeTaken(coll.collateralType, index, collIndex)
      ? `Only one ${collateralTypeLabel(coll.collateralType)} collateral is allowed per indemnitor.`
      : undefined;
  const update = (field) => (val) =>
    dispatch({ type: 'UPDATE_INDEMNITOR', index, field, value: val });

  const updateCollateral = (collIndex, field) => (val) =>
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field, value: val });

  const addCollateral = () =>
    dispatch({ type: 'ADD_COLLATERAL', indemnitorIndex: index });

  const removeCollateral = (collIndex) =>
    dispatch({ type: 'REMOVE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex });

  const copyDefendantAddress = (collIndex) => {
    if (!defendantAddress) return;
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field: 'collateralStreet1', value: defendantAddress.street1 || '' });
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field: 'collateralStreet2', value: defendantAddress.street2 || '' });
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field: 'collateralCity', value: defendantAddress.city || '' });
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field: 'collateralState', value: defendantAddress.state || '' });
    dispatch({ type: 'UPDATE_COLLATERAL', indemnitorIndex: index, collateralIndex: collIndex, field: 'collateralZip', value: defendantAddress.zip || '' });
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 relative">
      {/* Remove button */}
      {canRemove && !disabled && (
        <button
          onClick={onRemove}
          className="absolute top-3 right-3 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <XMarkIcon className="h-4 w-4 text-gray-400" />
        </button>
      )}

      <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
        Indemnitor #{index + 1}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <OathInput
          label="Relationship to Defendant"
          value={indemnitor.relationship}
          onChange={update('relationship')}
          placeholder="Relationship"
          required
          disabled={disabled}
          maxLength={LIMITS.INDEMNITOR_RELATIONSHIP}
        />
        <OathInput
          label="First Name"
          value={indemnitor.firstName}
          onChange={update('firstName')}
          placeholder="First name"
          disabled={disabled}
          maxLength={LIMITS.INDEMNITOR_FIRST_NAME}
        />
        <OathInput
          label="Last Name"
          value={indemnitor.lastName}
          onChange={update('lastName')}
          placeholder="Last name"
          disabled={disabled}
          maxLength={LIMITS.INDEMNITOR_LAST_NAME}
        />
        <OathInput
          label="Occupation"
          value={indemnitor.occupation}
          onChange={update('occupation')}
          placeholder="Occupation"
          disabled={disabled}
          maxLength={LIMITS.INDEMNITOR_OCCUPATION}
        />
        <OathInput
          label="Duration of Employment"
          value={indemnitor.durationOfEmployment}
          onChange={update('durationOfEmployment')}
          placeholder="e.g., 2 years 6 months"
          disabled={disabled}
        />
      </div>

      {/* Collaterals */}
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Collateral(s)</p>
        <div className="space-y-3">
          {indemnitor.collaterals.map((coll, collIndex) => (
            <div
              key={collIndex}
              className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Collateral #{collIndex + 1}
                </p>
                {indemnitor.collaterals.length > 1 && !disabled && (
                  <button
                    onClick={() => removeCollateral(collIndex)}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <XMarkIcon className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <OathSelect
                    label="Collateral Type"
                    value={coll.collateralType}
                    onChange={updateCollateral(collIndex, 'collateralType')}
                    options={typeOptionsFor(coll, collIndex)}
                    placeholder="Select type..."
                    required
                    disabled={disabled}
                    error={duplicateTypeError(coll, collIndex)}
                  />
                  {!duplicateTypeError(coll, collIndex) && typeOptionsFor(coll, collIndex).length < COLLATERAL_TYPES.length && (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      Types this indemnitor already has are hidden. Only Property Deeds can be added more than once.
                    </p>
                  )}
                </div>

                {/* Cash: show amount */}
                {coll.collateralType === 'CH' && (
                  <CurrencyInput
                    label="Amount"
                    value={coll.collateralAmount}
                    onChange={updateCollateral(collIndex, 'collateralAmount')}
                    required
                    disabled={disabled}
                  />
                )}

                {/* Property Deed: show address + liens */}
                {coll.collateralType === 'DE' && (
                  <>
                    <div className="sm:col-span-2 md:col-span-3">
                      <OathButton
                        variant="outline"
                        size="sm"
                        onClick={() => copyDefendantAddress(collIndex)}
                        disabled={disabled}
                      >
                        Same as Defendant Address?
                      </OathButton>
                    </div>
                    <div className="sm:col-span-2 md:col-span-3">
                      <OathInput
                        label="Street 1"
                        value={coll.collateralStreet1}
                        onChange={updateCollateral(collIndex, 'collateralStreet1')}
                        placeholder="Street address"
                        required
                        disabled={disabled}
                        maxLength={LIMITS.COLLATERAL_STREET1}
                      />
                    </div>
                    <div className="sm:col-span-2 md:col-span-3">
                      <OathInput
                        label="Street 2"
                        value={coll.collateralStreet2}
                        onChange={updateCollateral(collIndex, 'collateralStreet2')}
                        placeholder="Apt, suite, etc."
                        disabled={disabled}
                        maxLength={LIMITS.COLLATERAL_STREET2}
                      />
                    </div>
                    <OathInput
                      label="City"
                      value={coll.collateralCity}
                      onChange={updateCollateral(collIndex, 'collateralCity')}
                      placeholder="City"
                      required
                      disabled={disabled}
                      maxLength={LIMITS.COLLATERAL_CITY}
                    />
                    <OathSelect
                      label="State"
                      value={coll.collateralState}
                      onChange={updateCollateral(collIndex, 'collateralState')}
                      options={STATE_OPTIONS}
                      placeholder="Select state..."
                      required
                      disabled={disabled}
                    />
                    <OathInput
                      label="Zip Code"
                      value={coll.collateralZip}
                      onChange={(val) => {
                        if (/^\d{0,5}$/.test(val)) {
                          updateCollateral(collIndex, 'collateralZip')(val);
                        }
                      }}
                      placeholder="5-digit zip"
                      required
                      disabled={disabled}
                      maxLength={LIMITS.COLLATERAL_ZIP}
                    />
                    <div className="sm:col-span-2 md:col-span-3">
                      <OathInput
                        label="Liens"
                        value={coll.liens}
                        onChange={(val) => {
                          const numeric = val.replace(/[^0-9.]/g, '');
                          // Prevent multiple decimal points
                          const parts = numeric.split('.');
                          const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numeric;
                          updateCollateral(collIndex, 'liens')(sanitized);
                        }}
                        placeholder="Liens on property"
                        disabled={disabled}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {!disabled && (
          <OathButton
            variant="outline"
            size="sm"
            icon={PlusIcon}
            onClick={addCollateral}
            className="mt-2"
          >
            Add Collateral
          </OathButton>
        )}
      </div>
    </div>
  );
};

export default IndemnitorSection;
